import React, {
    useState,
    useCallback,
    useMemo,
    useEffect,
    useRef,
} from "react";
import ReactDOM from "react-dom";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { MoreHorizontal } from "lucide-react";
import PaginationControls from "../../../../components/ACS-Dashboard/ACS-Blocks/PaginationControls";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { rateApi } from "../api/rateApi";
import { routeApi } from "../api/routeApi";
import { laneApi } from "../api/laneApi";
import { contractApi } from "../api/contractApi";
import { accessorialApi } from "../api/accessorialApi";
import { usePagination } from "../hooks/usePagination";
import { useDataFetching } from "../hooks/useDataFetching";
import { formatAsLocalDate, formatCurrency } from "../utils/formatters";
import { safe } from "../utils/utils";
import { useDispatch } from "react-redux";
import { addTab } from "../../../../redux/slices/contractsSlice";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../../TableSavedViews";
import { buildFilterNode, countActiveFilters } from "../../../AdvancedFilter/filterTree";
import { AdvancedFilterToolbar } from "../../../AdvancedFilter/AdvancedFilterToolbar";
import { AdvancedFilterModal } from "../../../AdvancedFilter/AdvancedFilterModal";
import { SavedFiltersControl } from "../../../AdvancedFilter/SavedFiltersControl";
import { RATE_RULE_FIELDS } from "../ratesFilterFields";
import { OPTIONAL_RATE_COLS, getOptionalRateColumns } from "./ratesOptionalColumns";

const RATES_HEADERS = [
  "Selection",
  "Action",
  "Contract Code",
  "Lane Code",
  "Route Code",
  "Rate Code",
  "Office Code",
  "Charge Code",
  "Charge Name",
  "Value",
  "Currency",
  "Basis",
  "Revenue Basis",
  "Revenue Rate",
  "Carrier",
  "Shipper",
  "Carrier Code",
  "Shipper Code",
  "Agent Code",
  "Equipment",
  "Contracted",
  "Representative",
  "Effective Start",
  "Effective End",
  "Assoc.FSCs",
];

const SORTABLE_COLUMNS = {
  "Contract Code":  "route.lane.contract.contractCode",
  "Lane Code":      "route.lane.laneCode",
  "Route Code":     "route.routeCode",
  "Rate Code":      "rateCode",
  "Office Code":    "officeCode",
  "Charge Code":    "rateType",
  "Charge Name":    "rateType",
  "Value":          "cost",
  "Currency":       "currency",
  "Basis":          "rateBasis",
  "Revenue Basis":  "revenueBasis",
  "Revenue Rate":   "revenueRate",
  "Carrier":        "carrier",
  "Shipper":        "shipper",
  "Carrier Code":   "carrierCode",
  "Shipper Code":   "shipperCode",
  "Agent Code":     "agentCode",
  "Equipment":      "equipment",
  "Contracted":     "isContracted",
  "Representative": "isRepresentative",
  "Effective Start":"startDate",
  "Effective End":  "endDate",
  "Assoc.FSCs":     "fscAssociated",  // in-memory sort
};

const formatRateRow = (rate, accessorialMap) => {
  const isPercentage   = typeof rate.rateBasis === "string" && rate.rateBasis === "PERCENTAGE";
  const formattedValue = safe(() => isPercentage ? `${rate.cost}%` : formatCurrency(rate.cost, rate.currency), "string");

  return {
    ...rate,
    id:               safe(() => rate.id,         "number"),
    contractId:       safe(() => rate.contractId, "number"),
    "Contract Code":  safe(() => rate.contractCode),
    "Lane Code":      safe(() => rate.laneCode),
    "Route Code":     safe(() => rate.routeCode),
    "Rate Code":      safe(() => rate.rateCode),
    "Office Code":    safe(() => rate.officeCode),
    "Charge Code":    safe(() => rate.rateType),
    "Charge Name":    typeof rate.rateType === "string" && safe(() => accessorialMap[rate.rateType]?.accessorialName),
    "Condition Notes":typeof rate.rateType === "string" && safe(() => accessorialMap[rate.rateType]?.conditionNotes),
    Value:            formattedValue,
    Currency:         safe(() => rate.currency, "string", "USD"),
    Basis:            safe(() => rate.rateBasis),
    "Revenue Basis":  safe(() => rate.revenueBasis),
    "Revenue Rate":   safe(() => formatCurrency(rate.revenueRate, rate.currency), "string"),
    Carrier:          safe(() => rate.carrier),
    Shipper:          safe(() => rate.shipper),
    "Carrier Code":   safe(() => rate.carrierCode),
    "Shipper Code":   safe(() => rate.shipperCode),
    "Agent Code":     safe(() => rate.agentCode),
    Equipment:        safe(() => rate.equipment),
    Contracted:       safe(() => (rate.isContracted    ? "✓" : "✗")),
    Representative:   safe(() => (rate.isRepresentative? "✓" : "✗")),
    "Effective Start":safe(() => formatAsLocalDate(rate.startDate), "string", "-"),
    "Effective End":  safe(() => formatAsLocalDate(rate.endDate),   "string", "-"),
    "Assoc.FSCs":     safe(() => rate.fscAssociated, "number"),
  };
};

const RatesDetails = ({ officeCode, searchTerm = null, fetchFn = null }) => {
  const fallbackOfficeCode = "DIAMOND";
  const { pagination, setPagination, handlePageChange, handlePerPageChange } = usePagination();

  const [sortBy,     setSortBy]     = useState("updatedAt");
  const [ascending,  setAscending]  = useState(false);
  const [rates,          setRates]          = useState([]);
  const [selectedRates,  setSelectedRates]  = useState([]);
  const [isLoading,      setIsLoading]      = useState(true);
  const [accessorialMap, setAccessorialMap] = useState({});
  const [openMenu,       setOpenMenu]       = useState(null);
  const [dropdownPos,    setDropdownPos]    = useState({ top: 0, left: 0 });
  const [columnWidths, setColumnWidths] = useState({});
  const [advancedFilters, setAdvancedFilters] = useState({ rules: [], matchType: "AND" });
  const [showFilterModal, setShowFilterModal] = useState(false);

  const dropdownRef       = useRef(null);
  const checkboxHeaderRef = useRef(null);
  const dispatch          = useDispatch();
  const tableViews = useTableSavedViews({
    pageKey: TABLE_PAGE_KEYS.CONTRACT_RATES,
    allHeaders: RATES_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", "Contract Code"],
  });
  const {
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenMenu(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenMenu((prev) => (prev === id ? null : id));
  };

  const formattedRates = useMemo(
    () => rates.map((rate) => formatRateRow(rate, accessorialMap)),
    [rates, accessorialMap]
  );

  const activeOptionalCols = useMemo(() => getOptionalRateColumns(formattedRates), [formattedRates]);

  const activeRatesHeaders = useMemo(
    () => RATES_HEADERS.filter((h) => !OPTIONAL_RATE_COLS.includes(h) || activeOptionalCols.includes(h)),
    [activeOptionalCols]
  );

  const activeDataHeaders = useMemo(
    () => dataHeaders.filter((h) => !OPTIONAL_RATE_COLS.includes(h) || activeOptionalCols.includes(h)),
    [dataHeaders, activeOptionalCols]
  );

  const handleSort = (newSortBy) => {
    if (sortBy !== newSortBy) {
      setSortBy(newSortBy);
      setAscending(true);
    } else {
      setAscending((prev) => !prev);
    }
  };

  const onHeaderSortClick = (headerLabel) => {
    const key = SORTABLE_COLUMNS[headerLabel];
    if (!key) return;
    handleSort(key);
  };

  const handleRowDoubleClick = useCallback(async (rowId) => {
    const selectedRateData = formattedRates.find((r) => r.id === rowId);
    if (!selectedRateData) return;

    try {
      const rateOfficeCode  = selectedRateData.officeCode || officeCode;
      let contractIdToUse   = selectedRateData.contractId;

      if (contractIdToUse === "-") {
        const routeId = selectedRateData.routeId;
        if (!routeId) { console.error("No route ID:", selectedRateData); return; }
        try {
          const routeRes = await routeApi.getRouteById(rateOfficeCode, routeId);
          const laneId   = (routeRes.data || routeRes).laneId;
          const laneRes  = await laneApi.getLaneById(rateOfficeCode, laneId);
          contractIdToUse = (laneRes.data || laneRes).contractId;
        } catch (e) { console.error("Failed to resolve contract via route/lane:", e); return; }
      }

      if (!contractIdToUse) { console.error("No contract ID:", selectedRateData); return; }

      let contractData;
      try {
        const res  = await contractApi.getContractById(rateOfficeCode, contractIdToUse);
        contractData = res.data || res;
      } catch {
        contractData = {
          id:            contractIdToUse,
          contractCode:  selectedRateData["Contract Code"],
          officeCode:    rateOfficeCode,
          status:        "ACTIVE",
          effectiveDate: null, expirationDate: null,
        };
      }

      dispatch(addTab({
        tabData: {
          name:            `Contract: ${contractData.contractCode || selectedRateData["Contract Code"]}`,
          type:            "contractDetails",
          contractId:      contractIdToUse,
          officeCode:      rateOfficeCode,
          contractData,
          expandedDataKey: `rate-${rowId}`,
        },
      }));
    } catch (error) {
      console.error("Error opening contract details:", error);
    }
  }, [formattedRates, officeCode, dispatch]);

  // Memoized: buildFilterNode returns a new object every call; an unmemoized recompute
  // here changes fetchRates' identity every render, retriggering its effect in a loop.
  const filterNode = useMemo(
    () => buildFilterNode(RATE_RULE_FIELDS, advancedFilters),
    [advancedFilters]
  );

  const fetchRates = useCallback(
    (params) => {
      const apiParams = {
        page: pagination.currentPage - 1,
        size: pagination.perPage,
        sortBy,
        ascending,
        ...params,
      };
      if (searchTerm && fetchFn) return fetchFn({ query: searchTerm, status: "", filter: filterNode }, apiParams);
      return rateApi.searchRates({ filter: filterNode }, apiParams);
    },
    [searchTerm, fetchFn, pagination.currentPage, pagination.perPage, sortBy, ascending, filterNode]
  );

  useDataFetching({
    fetchFn: fetchRates,
    officeCode,
    pagination,
    onSuccess: useCallback((data) => {
      setRates(data.content);
      setPagination((prev) => ({ ...prev, total: data.totalElements }));
      setIsLoading(false);
    }, [setPagination]),
    onError: (error) => { console.error("Error fetching rates:", error); setIsLoading(false); },
  });

  const getAccessorialMap = useCallback(async () => {
    const tryFetch = async (code, isFallback = false) => {
      try {
        const res = await accessorialApi.getAccessorials(code);
        if (Array.isArray(res.data) && res.data.length > 0) {
          return res.data.reduce((map, { accessorialCode, accessorialName, conditionNotes }) => {
            map[accessorialCode] = { accessorialName, conditionNotes };
            return map;
          }, {});
        }
      } catch (err) { console.error(`Error fetching accessorials for ${code}:`, err); }
      return null;
    };
    const map = await tryFetch(officeCode) || await tryFetch(fallbackOfficeCode, true);
    setAccessorialMap(map || {});
  }, [officeCode]);

  useEffect(() => { getAccessorialMap(); }, [getAccessorialMap]);

  const handleSelectAll      = useCallback((checked) => setSelectedRates(checked ? formattedRates.map((r) => r.id) : []), [formattedRates]);
  const handleSingleCheckbox = useCallback((id) => setSelectedRates((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]), []);

  return (
    <div className="bg-white dark:bg-acs-dark-surface p-1 rounded-xl shadow-md flex flex-col h-full transition-none">
      {isLoading ? (
        <Loader color="text-blue-600 dark:text-acs-dark-text" size="h-5 w-5" message="Loading Rates..." />
      ) : (
        <>
          {!searchTerm && (
            <AdvancedFilterToolbar
              ruleFields={RATE_RULE_FIELDS}
              filters={advancedFilters}
              activeCount={countActiveFilters(RATE_RULE_FIELDS, advancedFilters)}
              onOpenModal={() => setShowFilterModal(true)}
              onClearAll={() => setAdvancedFilters({ rules: [], matchType: "AND" })}
              onChangeFilters={setAdvancedFilters}
              trailing={
                <div className="flex items-center gap-2">
                  <SavedFiltersControl
                    pageKey={TABLE_PAGE_KEYS.CONTRACT_RATES}
                    ruleFields={RATE_RULE_FIELDS}
                    currentFilters={advancedFilters}
                    onApply={setAdvancedFilters}
                  />
                  <TableSavedViewsControl
                    controlLabel={controlLabel}
                    views={views}
                    activeView={activeView}
                    loading={viewsLoading}
                    error={viewsError}
                    isDirty={isDirty}
                    allDataHeaders={activeRatesHeaders.slice(2)}
                    dataHeaders={activeDataHeaders}
                    onSelectView={selectView}
                    onSaveNew={saveNewView}
                    onUpdateActive={updateActiveView}
                    onRename={renameView}
                    onDelete={deleteView}
                    onSetDefault={setDefaultView}
                    onUpdateColumnVisibility={updateColumnVisibility}
                    onManageColumns={onManageColumns}
                  />
                </div>
              }
            />
          )}

          {searchTerm && (
            <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
              <TableSavedViewsControl
                controlLabel={controlLabel}
                views={views}
                activeView={activeView}
                loading={viewsLoading}
                error={viewsError}
                isDirty={isDirty}
                allDataHeaders={activeRatesHeaders.slice(2)}
                dataHeaders={activeDataHeaders}
                onSelectView={selectView}
                onSaveNew={saveNewView}
                onUpdateActive={updateActiveView}
                onRename={renameView}
                onDelete={deleteView}
                onSetDefault={setDefaultView}
                onUpdateColumnVisibility={updateColumnVisibility}
                onManageColumns={onManageColumns}
              />
            </div>
          )}
{showFilterModal && (
            <AdvancedFilterModal
              ruleFields={RATE_RULE_FIELDS}
              filters={advancedFilters}
              onApply={setAdvancedFilters}
              onClose={() => setShowFilterModal(false)}
            />
          )}
          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
            <Table
              {...columnManagerProps}
              headers={activeRatesHeaders}
              data={formattedRates}
              initialColumnWidths={columnWidths}
              onColumnResize={setColumnWidths}
              enableColumnResize
              enableColumnReorder
              enableColumnPin
              selectedRows={selectedRates}
              totalValidRows={formattedRates.length}
              onSelectAll={handleSelectAll}
              renderCheckboxActions={() => {}}
              checkboxHeaderRef={checkboxHeaderRef}
              sortBy={sortBy}
              ascending={ascending}
              onSort={onHeaderSortClick}
              sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
              sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
            >
              {(row, _rowIdx, _columnWidths, getStickyCellProps, visibleColumns) => {
                const id     = row.id;
                const isOpen = openMenu === id;
                const orderedDataHeaders = (visibleColumns ?? [])
                  .map((col) => col.id)
                  .filter((id) => id !== "Selection" && id !== "Action");
                const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : activeDataHeaders;

                return (
                  <>
                    {/* Column 0 — Checkbox */}
                    <td
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${getStickyCellProps("Selection").className}`}
                      style={{ ...getStickyCellProps("Selection").style, width: 80, minWidth: 80, maxWidth: 80 }}
                    >
                      <input
                        type="checkbox"
                        className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                        checked={selectedRates.includes(id)}
                        onChange={() => handleSingleCheckbox(id)}
                      />
                    </td>

                    {/* Column 1 — Action (portal dropdown) */}
                    <td
                      onClick={(e) => e.stopPropagation()}
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${getStickyCellProps("Action").className}`}
                      style={{ ...getStickyCellProps("Action").style, width: 50, minWidth: 50, maxWidth: 50 }}
                    >
                      <button
                        data-action-trigger
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                        onClick={(e) => handleActionClick(id, e)}
                      >
                        <MoreHorizontal size={18} className="mx-auto" />
                      </button>
                    </td>

                    {isOpen && ReactDOM.createPortal(
                      <div
                        ref={dropdownRef}
                        style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
                      >
                        <ActionMenu
                          rowId={id}
                          isOpen
                          moduleName="Rate"
                          onClose={() => setOpenMenu(null)}
                          actions={[
                            { icon: "Eye", label: "View", color: "blue", action: () => handleRowDoubleClick(id) },
                          ]}
                        />
                      </div>,
                      document.body
                    )}

                    {/* Columns 2+ — Data */}
                    {dataHeadersToRender.map((col) => {
                      return (
                        <td
                          key={col}
                          className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${getStickyCellProps(col).className}`}
                          style={{
                            ...getStickyCellProps(col).style,
                            whiteSpace:   "nowrap",
                            overflow:     "hidden",
                            textOverflow: "ellipsis",
                            width:        "200px",
                            minWidth:     "200px",
                            maxWidth:     "200px",
                          }}
                          onDoubleClick={() => handleRowDoubleClick(id)}
                        >
                          {row[col]}
                        </td>
                      );
                    })}
                  </>
                );
              }}
            </Table>
          </div>

          <PaginationControls
            pagination={pagination}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            moduleName="Rates"
            className="mt-4 p-2 border-t border-gray-200 dark:border-acs-dark-border"
          />
        </>
      )}
    </div>
  );
};

export default RatesDetails;